import type * as types from './types';

import { assertIsPath, type Path } from '@/src/lib/urls';

import * as errors from './errors';
import { type ConcretePath, constructConcretePath, type PathParameters } from './patterns';
import { isPageActive as detachedIsPageActive } from './util';

interface GetPageOptions {
  readonly strict?: boolean;
}

type NonEmptyArray<T> = [T, ...T[]];

const isHyphenCase = (value: string) => /^[a-z]+(-[a-z]+)*$/.test(value);

const findDuplicates = <T>(array: T[], predicate: (a: T, b: T) => boolean): T[] => {
  const seen: T[] = [];
  const duplicates: T[] = [];
  for (const curr of array) {
    if (seen.some(value => predicate(value, curr))) {
      if (!duplicates.some(value => predicate(value, curr))) {
        duplicates.push(curr);
      }
    } else {
      seen.push(curr);
    }
  }
  return duplicates;
};

const ensureArrayIsNonEmpty = <T>(value: T[]): NonEmptyArray<T> => {
  if (value.length === 0) {
    throw new Error(
      'Encountered an empty array when it was expected that the array have a non-zero length!',
    );
  }
  return value as NonEmptyArray<T>;
};

/**
 * Constructs a valid {@link types.IPagesConfiguration} object using the provided pages
 * configuration array, {@link T}, after ensuring that the provided array is valid.
 * The validation that this method performs ensures that the application does not start or build
 * if the pages configuration array is invalid.
 *
 * @throws {@link errors.NonUniquePageConfigurationError}
 *   If duplicate values are detected for unique fields in the configuration.
 */
export const configurePages = <
  T extends types.PagesConfigurationArrayInput<P>,
  P extends types.PageConfiguration,
>(
  pages: T,
): types.IPagesConfiguration<P, T> => {
  const pagesWithDuplicateIds = findDuplicates([...pages], (a, b) => a.id === b.id);
  if (pagesWithDuplicateIds.length > 0) {
    throw new errors.NonUniquePageConfigurationError(
      'id',
      ensureArrayIsNonEmpty(pagesWithDuplicateIds.map(p => p.id)),
    );
  }
  const pagesWithDuplicatePaths = findDuplicates([...pages], (a, b) => a.path === b.path);
  if (pagesWithDuplicatePaths.length > 0) {
    throw new errors.NonUniquePageConfigurationError(
      'path',
      ensureArrayIsNonEmpty(pagesWithDuplicatePaths.map(p => p.path)),
    );
  }

  const pagesWithInvalidIds = pages.filter(page => !isHyphenCase(page.id));
  if (pagesWithInvalidIds.length > 0) {
    throw new errors.InvalidPageIdFormatError(
      ensureArrayIsNonEmpty(pagesWithInvalidIds.map(p => p.id)),
    );
  }

  const isPageId = (id: unknown): id is T[number]['id'] => pages.some(p => p.id === id);

  const pageMatchesPath = (page: T[number] | T[number]['id'], path: string): boolean => {
    const pg = isPageId(page) ? get(page) : page;
    return typeof pg.matcher === 'function' ? pg.matcher(path) : pg.matcher.test(path);
  };

  function get<Lookup extends T[number]['id']>(lookup: Lookup): types.GetPageByIdResult<T, Lookup>;

  function get<O extends GetPageOptions>(
    lookup: { readonly path: string },
    options?: O,
  ): types.GetPageByPathResult<T, O>;

  function get<Lookup extends T[number]['id'], O extends GetPageOptions>(
    lookup: { readonly path: string } | Lookup,
    options?: O,
  ): types.GetPageByIdResult<T, Lookup> | types.GetPageByPathResult<T, O> {
    if (typeof lookup === 'string') {
      const matches = pages.filter(page => page.id === lookup);
      if (matches.length > 1) {
        throw new errors.NonUniquePageIdError(
          lookup,
          ensureArrayIsNonEmpty(matches.map(m => m.path)),
        );
      } else if (matches.length === 0) {
        /* This should never happen in practice, as it is guaranteed to not be a condition by
           TypeScript's type system. */
        throw new errors.InvalidPageIdError(lookup);
      }
      return matches[0];
    }

    const filtered = [...pages].filter(page => pageMatchesPath(page, lookup.path));
    if (filtered.length > 1) {
      throw new errors.MultiplePagesMatchPathError(
        lookup.path,
        ensureArrayIsNonEmpty(filtered.map(page => page.id)),
      );
    }
    if (filtered.length === 0) {
      if (options?.strict) {
        throw new errors.PageForPathNotFoundError(lookup.path);
      }
      const result: types.GetPageByPathResult<T, { readonly strict?: false }> = null;
      return result as types.GetPageByPathResult<T, O>;
    }

    return filtered[0];
  }

  return {
    all: pages,
    constructPath<
      I extends T[number]['id'],
      Params extends PathParameters<Extract<T[number], { id: I }>['path']>,
    >(id: I, params: Params): ConcretePath<Extract<T[number], { id: I }>['path'], Params> {
      const page = this.get(id);
      const constructed = constructConcretePath<Extract<T[number], { id: I }>['path'], Params>(
        page.path,
        params,
      );
      assertIsPath(constructed);
      return constructed;
    },
    get,
    isPageId,
    pageMatchesPath,
  };
};

/**
 * Constructs a valid {@link types.IUiAwarePagesConfiguration} object using the provided pages
 * configuration array, {@link T}, after ensuring that the provided array is valid.
 * The validation that this method performs ensures that the application does not start or build
 * if the pages configuration array is invalid.
 *
 * The {@link configureUiAwarePages} method differs from the {@link configurePages} method in that
 * it provides additional UI-aware behavior, such as determining the active pages based on the
 * current pathname.
 *
 * @throws {@link errors.NonUniquePageConfigurationError}
 *   If duplicate values are detected for unique fields in the configuration.
 */
export const configureUiAwarePages = <
  T extends types.PagesConfigurationArrayInput<P>,
  P extends types.UiAwarePageConfiguration,
>(
  pages: T,
): types.IUiAwarePagesConfiguration<P, T> => {
  const pagesConfig = configurePages(pages);

  return {
    ...pagesConfig,
    getActivePages(pathname: Path): T[number][] {
      return [...this.all].reduce(
        (acc: T[number][], page) => (this.isPageActive(page, pathname) ? [...acc, page] : acc),
        [],
      );
    },
    isPageActive(page: T[number] | T[number]['id'], pathname: string) {
      const pg = typeof page === 'string' ? this.get(page) : page;
      return detachedIsPageActive(pg, pathname);
    },
  };
};
